import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import ProtectedAdminRoute from './components/common/ProtectedAdminRoute';
import LocaleLayout from './components/common/LocaleLayout';
import RouteFallback from './components/common/RouteFallback';
import { marketingRouteElements } from './routes/marketingRoutes';
import MatomoTracker from './components/common/MatomoTracker';
import ErrorBoundary from './components/common/ErrorBoundary';

const MagicLinkVerifyPage = lazy(() => import('./pages/MagicLinkVerifyPage'));
const MagicLinkSentPage = lazy(() => import('./pages/MagicLinkSentPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const WorkspaceSetupWrapper = lazy(() => import('./components/onboarding/WorkspaceSetupWrapper'));
const GaleneAuthPortal = lazy(() => import('./pages/GaleneAuthPortal'));
const JoinQuickPage = lazy(() => import('./pages/JoinQuickPage'));
const WatchPage = lazy(() => import('./pages/WatchPage'));
const ClientLayout = lazy(() => import('./components/client/ClientLayout'));
const Dashboard = lazy(() => import('./pages/client/Dashboard'));
const MyLives = lazy(() => import('./pages/client/MyLives'));
const LiveDetail = lazy(() => import('./pages/client/LiveDetail'));
const Statistics = lazy(() => import('./pages/client/Statistics'));
const Profile = lazy(() => import('./pages/client/Profile'));
const Settings = lazy(() => import('./pages/client/Settings'));
const Help = lazy(() => import('./pages/client/Help'));
const Ideas = lazy(() => import('./pages/client/Ideas'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ClientsPage = lazy(() => import('./pages/admin/ClientsPage'));
const ClientDetailPage = lazy(() => import('./pages/admin/ClientDetailPage'));
const InstancesPage = lazy(() => import('./pages/admin/InstancesPage'));
const InstanceDetailPage = lazy(() => import('./pages/admin/InstanceDetailPage'));
const LiveSessionsPage = lazy(() => import('./pages/admin/LiveSessionsPage'));
const LiveStatsPage = lazy(() => import('./pages/admin/LiveStatsPage'));
const ForbiddenPage = lazy(() => import('./pages/errors/ForbiddenPage'));
const NotFoundPage = lazy(() => import('./pages/errors/NotFoundPage'));

function App() {
  return (
    <AuthProvider>
      <Router>
        <ErrorBoundary>
          <MatomoTracker />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Marketing FR (racine) */}
              <Route path="/" element={<LocaleLayout />}>
                {marketingRouteElements}
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* Marketing EN (/en) */}
              <Route path="/en" element={<LocaleLayout />}>
                {marketingRouteElements}
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/magic-link-sent" element={<MagicLinkSentPage />} />
              <Route path="/verify/:token" element={<MagicLinkVerifyPage />} />
              <Route path="/auth/galene" element={<GaleneAuthPortal />} />
              <Route path="/j/:code" element={<JoinQuickPage />} />
              <Route path="/watch/:liveId" element={<WatchPage />} />
              <Route
                path="/workspace-setup"
                element={
                  <ProtectedRoute>
                    <WorkspaceSetupWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <ClientLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="lives" element={<MyLives />} />
                <Route path="lives/:id" element={<LiveDetail />} />
                <Route path="statistics" element={<Statistics />} />
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={<Settings />} />
                <Route path="help" element={<Help />} />
                <Route path="ideas" element={<Ideas />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              <Route
                path="/admin"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout />
                  </ProtectedAdminRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="clients/:id" element={<ClientDetailPage />} />
                <Route path="instances" element={<InstancesPage />} />
                <Route path="instances/:id" element={<InstanceDetailPage />} />
                <Route path="lives" element={<LiveSessionsPage />} />
                <Route path="lives/:id/stats" element={<LiveStatsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              <Route path="/403" element={<ForbiddenPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

export default App;
